import { useMutation, useQuery } from 'react-query'
import axios from 'axios'
import {
  InsuranceParamsDto,
  WizardVehicleAdditionalData,
  CreateContractRequest,
  CreateContractResponse,
  CreateContractAdditionallyRequest,
  CreateContractAdditionallyResponse,
  LastWSProps,
  SaveStep2Request,
  Addon,
  GetWarningRequest,
  Step1Warning,
  Step1CategoryWarning,
  BaseParamsDto,
  InsuranceParamsPropertyDto,
  WizardPropertyAdditionalData,
  Step2Property,
  WizardTravelAdditionalData,
  SaveStep2TravelRequest,
  InsuranceParamsTravelDto,
  Property,
  CalcMinAmountHouseholdRequest,
  CalcAmountResponse,
  CalcMinAmountRealtyRequest,
  Step1PropertyWarning,
  SimpleItem,
  ContractEmailLog,
  ConfirmContractRequest,
  ConfirmContractResponse,
  IsContractConfirmedResponse,
} from 'BrokerTsLib/src/dtos'
import { baseUrl, GenericQueryOptions } from '.'
import { OkAppsMeetingRecord } from '../dtos/OkAppsMeetingRecord'
import { useApp } from './useApp'
import { ContractDocType, Product } from 'BrokerTsLib/src/enums'

export type UpdateAddonsRequest = {
  paymentFrequency?: number
  addons?: Addon[]
}

const wizardApiUrl = process.env.REACT_APP_WIZARD_API
const wizardPropertyApiUrl = process.env.REACT_APP_WIZARD_PROPERTY_API
const wizardTravelApiUrl = process.env.REACT_APP_WIZARD_TRAVEL_API

export const useUrl = () => {
  const { product } = useApp()

  let url: string | undefined = ''

  switch (product) {
    case Product.TravelInsurance:
      url = wizardTravelApiUrl
      break
    case Product.PropertyInsurance:
      url = wizardPropertyApiUrl
      break
    default:
      url = wizardApiUrl
      break
  }

  return `${baseUrl}/${url}`
}

export const useGetInsuranceParams = (
  paramsId: string,
  options?: GenericQueryOptions<
    InsuranceParamsDto | InsuranceParamsPropertyDto | InsuranceParamsTravelDto
  >,
) => {
  const url = useUrl()

  return useQuery(
    ['getInsuranceParams', paramsId],
    () =>
      axios.get<InsuranceParamsDto | InsuranceParamsPropertyDto | InsuranceParamsTravelDto>(
        `${url}/GetInsuranceParams?paramsId=${paramsId}`,
      ),
    options,
  )
}

export const useSaveInsuranceParams = () => {
  const url = useUrl()

  return useMutation(
    (insuranceParams: InsuranceParamsDto | InsuranceParamsPropertyDto | InsuranceParamsTravelDto) =>
      axios.post(`${url}/SaveInsuranceParams`, insuranceParams),
  )
}

export const useGetStep4 = (
  paramsId: string,
  options?: GenericQueryOptions<
    WizardVehicleAdditionalData | WizardPropertyAdditionalData | WizardTravelAdditionalData
  >,
) => {
  const url = useUrl()
  return useQuery(
    ['getStep4', paramsId],
    () =>
      axios.get<
        WizardVehicleAdditionalData | WizardPropertyAdditionalData | WizardTravelAdditionalData
      >(`${url}/GetStep4?paramsId=${paramsId}`),
    options,
  )
}

export const useSaveStep4 = () => {
  const url = useUrl()
  return useMutation(
    (
      step4:
        | WizardVehicleAdditionalData
        | WizardPropertyAdditionalData
        | WizardTravelAdditionalData,
    ) => axios.post(`${url}/SaveStep4`, step4),
  )
}

export const useGetMeetingRecord = (
  paramsId: string,
  options?: GenericQueryOptions<OkAppsMeetingRecord>,
) => {
  const url = useUrl()
  return useQuery(
    ['getMeetingRecord', paramsId],
    () => axios.get<OkAppsMeetingRecord>(`${url}/GetMeetingRecord?paramsId=${paramsId}`),
    options,
  )
}

export const useSaveMeetingRecord = () => {
  const url = useUrl()
  return useMutation((meetingRecord: OkAppsMeetingRecord) =>
    axios.post(`${url}/SaveMeetingRecord`, meetingRecord),
  )
}

export const useCreateContract = () => {
  const url = useUrl()
  return useMutation((request: CreateContractRequest) =>
    axios.post<CreateContractResponse>(`${url}/CreateContract`, request),
  )
}

export const useCreateContractAdditionally = () => {
  const url = useUrl()
  return useMutation((request: CreateContractAdditionallyRequest) =>
    axios.post<CreateContractAdditionallyResponse>(`${url}/CreateContractAdditionally`, request),
  )
}

export const useUpdateLastWizardStep = () => {
  const url = useUrl()
  return useMutation((data: LastWSProps) => axios.post(`${url}/UpdateLastWizardStep`, data))
}

export const useGetAddons = (paramsId: string, options?: GenericQueryOptions<Addon[]>) => {
  const url = useUrl()
  return useQuery(
    ['useGetAddons', paramsId],
    () => axios.get<Addon[]>(`${url}/GetAddons?paramsId=${paramsId}`),
    options,
  )
}

export const useUpdateAddons = (paramsId: string) => {
  const url = useUrl()
  return useMutation((data: UpdateAddonsRequest) =>
    axios.post(`${url}/UpdateAddons?paramsId=${paramsId}`, data),
  )
}

export const useSaveStep2 = () => {
  const url = useUrl()
  return useMutation((data: SaveStep2Request | Step2Property | SaveStep2TravelRequest) =>
    axios.post(`${url}/SaveStep2`, data),
  )
}

export const useAddVariant = (paramsId: string) => {
  const url = useUrl()
  return useMutation((data: BaseParamsDto | Property) =>
    axios.post(`${url}/AddVariant?paramsId=${paramsId}`, data),
  )
}

export const useGetStep1Warnings = () => {
  const url = useUrl()
  return useMutation((data: GetWarningRequest) =>
    axios.post<Step1Warning[]>(`${url}/GetWarnings`, data),
  )
}

export const useGetStep1PropertyWarnings = (
  options?: GenericQueryOptions<Step1PropertyWarning[]>,
) => {
  const url = useUrl()
  return useQuery(
    ['GetWarnings'],
    () => axios.get<Step1PropertyWarning[]>(`${url}/GetWarnings`),
    options,
  )
}

export const useGetStep1CategoryWarnings = () => {
  const url = useUrl()
  return useMutation((data: GetWarningRequest) =>
    axios.post<Step1CategoryWarning[]>(`${url}/GetCategoryWarnings`, data),
  )
}

export const useUpdateInsuranceStartDate = (paramsId: string) => {
  const url = useUrl()
  return useMutation({
    mutationFn: (date: Date) =>
      axios.post(`${url}/UpdateInsuranceStartDate`, { insuranceStartDate: date, paramsId }),
  })
}

export const useGetVehicleInspectionDistricts = (
  offerId: string,
  options?: GenericQueryOptions<SimpleItem[]>,
) => {
  const url = useUrl()

  return useQuery(
    ['useGetVehicleInspectionDistricts', offerId],
    () => axios.get<SimpleItem[]>(`${url}/GetVehicleInspectionDistricts?offerId=${offerId}`),
    options,
  )
}

// jen pro majetek
export const useCalcRecommendedAmountHousehold = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (data: CalcMinAmountHouseholdRequest) =>
      axios.post<CalcAmountResponse>(`${url}/CalcRecommendedAmountHousehold`, data),
  })
}

// jen pro majetek
export const useCalcRecommendedAmountRealty = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (data: CalcMinAmountRealtyRequest) =>
      axios.post<CalcAmountResponse>(`${url}/CalcRecommendedAmountRealty`, data),
  })
}

export const useGetDocumentTypes = (offerId: number) => {
  const url = useUrl()

  return useQuery(['documentTypes', offerId], () =>
    axios.get<{ type: ContractDocType; description: string }[]>(`${url}/GetDocumentTypes`, {
      params: { offerId },
    }),
  )
}

export const useUploadInsuranceOfferDocument = () => {
  const url = useUrl()

  return useMutation(
    (x: { data: FormData; offerId: number; documentType: ContractDocType; overwrite: boolean }) =>
      axios.post(`${url}/UploadInsuranceOfferDocument`, x.data, {
        params: {
          offerId: x.offerId,
          documentType: x.documentType,
          overwrite: x.overwrite,
        },
      }),
  )
}

export const useGetContractEmailLogs = (
  paramsId: number,
  options?: GenericQueryOptions<ContractEmailLog[]>,
) => {
  const url = useUrl()
  return useQuery(
    ['useGetContractEmailLogs', paramsId],
    () => axios.get<ContractEmailLog[]>(`${url}/GetContractEmailLogs?paramsId=${paramsId}`),
    options,
  )
}

export const useConfirmContract = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (data: ConfirmContractRequest) =>
      axios.post<ConfirmContractResponse>(`${url}/ConfirmContract`, data),
  })
}

export const useGetIsContractConfirmed = (
  paramsId: number,
  options?: GenericQueryOptions<IsContractConfirmedResponse>,
) => {
  const url = useUrl()
  return useQuery(
    ['useGetIsContractConfirmed', paramsId],
    () => axios.get<IsContractConfirmedResponse>(`${url}/IsContractConfirmed?paramsId=${paramsId}`),
    options,
  )
}